import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { CircularProgress } from '@material-ui/core'
import SydModal from '../../../../commonDesign/SydModal'
import SydButton from '../../../../commonDesign/Button'
import Icon from '../../../../atoms/Icon'
import { useArchiveViewVersion, useListViewVersions, useUnarchiveViewVersion } from '../../../../../api/abundanceEngine'
import { useFormattingContext } from '../../../../organisms/FormattingProvider/FormattingContext'

function ManageVersionsModal ({ open, onClose, viewId, onAlert }) {
  const { formatter } = useFormattingContext()

  const [showArchived, setShowArchived] = useState(false)
  const [pendingAction, setPendingAction] = useState(null)
  const [pendingVersionId, setPendingVersionId] = useState(null)
  const { data: versions = [], isLoading } = useListViewVersions(viewId, {
    includeArchived: true
  })
  const archiveVersion = useArchiveViewVersion()
  const unarchiveVersion = useUnarchiveViewVersion()

  const filteredVersions = useMemo(() => {
    if (showArchived) return versions
    return versions.filter(v => !v.archivedAt)
  }, [versions, showArchived])

  const handleArchive = useCallback(async (viewVersionId) => {
    setPendingAction('archive')
    setPendingVersionId(viewVersionId)
    try {
      await archiveVersion.mutateAsync({ viewVersionId })
      onAlert?.({ severity: 'success', message: 'Version archived' })
    } catch (err) {
      onAlert?.({ severity: 'error', message: err?.message || 'Failed to archive version' })
    } finally {
      setPendingAction(null)
      setPendingVersionId(null)
    }
  }, [archiveVersion, onAlert])

  const handleUnarchive = useCallback(async (viewVersionId) => {
    setPendingAction('unarchive')
    setPendingVersionId(viewVersionId)
    try {
      await unarchiveVersion.mutateAsync({ viewVersionId })
      onAlert?.({ severity: 'success', message: 'Version unarchived' })
    } catch (err) {
      onAlert?.({ severity: 'error', message: err?.message || 'Failed to unarchive version' })
    } finally {
      setPendingAction(null)
      setPendingVersionId(null)
    }
  }, [unarchiveVersion, onAlert])

  return (
    <SydModal
      open={open}
      onClose={onClose}
      title='Manage Versions'
      subtitle='Archive or unarchive view versions.'
      size='medium'
    >
      <div className='flex flex-col gap-md'>
        <label className='flex items-center gap-1 text-xs cursor-pointer' style={{ color: 'var(--color-neutral-600)' }}>
          <input
            type='checkbox'
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
          />
          Show archived versions
        </label>

        {isLoading ? (
          <div className='flex items-center justify-center p-4'>
            <CircularProgress size={24} />
          </div>
        ) : !filteredVersions.length ? (
          <p className='font-size-md' style={{ color: 'var(--color-neutral-500)' }}>No versions found.</p>
        ) : (
          <div className='flex flex-col gap-sm' style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {filteredVersions.map((v) => (
              <div
                key={v.viewVersionId}
                className='flex items-center justify-between gap-3 p-2 rounded'
                style={{
                  backgroundColor: v.archivedAt ? 'var(--color-neutral-100)' : 'var(--color-surface-050)',
                  border: '1px solid var(--color-neutral-200)',
                  opacity: v.archivedAt ? 0.7 : 1
                }}
              >
                <div className='flex items-center gap-2'>
                  {!!v.isLive && (
                    <span
                      className='text-xs px-1 rounded bg-primary-400 text-contrast-primary-400'
                    >
                      Live
                    </span>
                  )}
                  <div className='flex-1 flex flex-col'>
                    <span className='font-size-xs'>{formatter(v.modifiedDate, '@MM/DD/YY')} Version</span>

                    <span className='font-size-xs'>Modified by {v.modifiedByUser ?? v.modifiedBy}</span>

                    {v.description && (
                      <span className='text-xs' style={{ color: 'var(--color-neutral-500)' }}>
                        {v.description}
                      </span>
                    )}
                  </div>
                  {v.archivedAt && (
                    <span
                      className='text-xs px-1 rounded'
                      style={{ backgroundColor: 'var(--color-neutral-200)', color: 'var(--color-neutral-600)' }}
                    >
                      Archived
                    </span>
                  )}
                </div>
                <div className='flex gap-1'>
                  {!v.isLive && !v.archivedAt && (
                    <SydButton
                      size='sm'
                      variant='primary'
                      priority='warning'
                      icon='archive'
                      onClick={() => handleArchive(v.viewVersionId)}
                      processing={pendingAction === 'archive' && pendingVersionId === v.viewVersionId}
                      disabled={pendingAction === 'archive' && pendingVersionId === v.viewVersionId}
                    >
                      Archive
                    </SydButton>
                  )}
                  {v.archivedAt && (
                    <SydButton
                      size='sm'
                      variant='secondary'
                      onClick={() => handleUnarchive(v.viewVersionId)}
                      processing={pendingAction === 'unarchive' && pendingVersionId === v.viewVersionId}
                      disabled={pendingAction === 'unarchive' && pendingVersionId === v.viewVersionId}
                    >
                      <span className='flex items-center gap-1'>
                        <Icon name='history' customSize='14px' />
                        Unarchive
                      </span>
                    </SydButton>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </SydModal>
  )
}

ManageVersionsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  viewId: PropTypes.number,
  onAlert: PropTypes.func
}

export default ManageVersionsModal
